import type { TimelineEntity, ViewState } from "./Timeline.types";
import { getCategoryColor } from "./utils/colors";

export const ROW_HEIGHT = 50;
export const ENTITY_START_Y = 42; // axisY(26) + 16
const TEXT_FONT_PX = 16;
const LABEL_FONT_PX = 16;
const TEXT_FONT_FAMILY = "-apple-system, BlinkMacSystemFont, sans-serif";
const BOLD_TEXT_FONT = `bold ${TEXT_FONT_PX}px ${TEXT_FONT_FAMILY}`;
const BOLD_LABEL_FONT = `bold ${LABEL_FONT_PX}px ${TEXT_FONT_FAMILY}`;
const MIN_ENTITY_WIDTH = 20;

type YearToXFn = (year: number) => number;

export type TimelineLabels = {
  startYear: (year: string) => string;
  endYear: (year: string) => string;
  age: (age: number) => string;
  categories: (categories: string) => string;
};

type RenderParams = {
  ctx: CanvasRenderingContext2D;
  width: number;
  height: number;
  viewState: ViewState;
  entities: TimelineEntity[];
  hoveredEntity: TimelineEntity | null;
  mousePos: { x: number; y: number };
  minYear: number;
  maxYear: number;
  yearRange: number;
  yearToX: YearToXFn;
  labels: TimelineLabels;
};

export const entityToScreen = (
  entity: TimelineEntity,
  yearToX: YearToXFn,
  viewState: ViewState,
): { x: number; y: number } => {
  const axisY = 26;
  const entityStartY = axisY + 16;
  const x = yearToX(entity.start.year) * viewState.scale + viewState.offsetX;
  const y = entityStartY + (entity.rowNumber || 0) * ROW_HEIGHT + viewState.offsetY;
  return { x, y };
};

const drawEntities = (params: RenderParams) => {
  const { ctx, width, height, viewState, entities, hoveredEntity, yearToX } = params;
  const axisY = 26;
  const borderY = axisY + 12;

  ctx.save();
  ctx.beginPath();
  ctx.rect(0, borderY, width, height - borderY);
  ctx.clip();

  entities.forEach((entity) => {
    // Y-first cull — rowY depends only on rowNumber, no yearToX needed.
    const rowY = ENTITY_START_Y + (entity.rowNumber || 0) * ROW_HEIGHT + viewState.offsetY;
    if (rowY < -50 || rowY > height + 50) return;

    const hasEndDate = entity.end && entity.end.year !== entity.start.year;

    // Compute screen X coords once; reuse for both cull and drawing.
    const startXScreen = yearToX(entity.start.year) * viewState.scale + viewState.offsetX;

    if (hasEndDate) {
      const endXScreen = yearToX(entity.end?.year ?? entity.start.year) * viewState.scale + viewState.offsetX;
      const left = Math.min(startXScreen, endXScreen);
      const right = left + Math.max(MIN_ENTITY_WIDTH * 3, endXScreen - left);
      if (right < -50 || left > width + 50) return;

      const color = getCategoryColor(entity.categoryNames ?? entity.categories);
      const isHovered = hoveredEntity?.id === entity.id;
      const entityWidth = Math.max(MIN_ENTITY_WIDTH * 3, endXScreen - startXScreen);
      const entityHeight = 26;
      const entityRadius = entityHeight / 2;
      const pillX = startXScreen;
      const pillY = rowY;

      ctx.fillStyle = isHovered ? "#6EE7B7" : color;
      ctx.globalAlpha = 0.3;
      ctx.beginPath();
      ctx.moveTo(pillX + entityRadius, pillY - entityHeight / 2);
      ctx.lineTo(pillX + entityWidth - entityRadius, pillY - entityHeight / 2);
      ctx.arc(pillX + entityWidth - entityRadius, pillY, entityRadius, -Math.PI / 2, Math.PI / 2);
      ctx.lineTo(pillX + entityRadius, pillY + entityHeight / 2);
      ctx.arc(pillX + entityRadius, pillY, entityRadius, Math.PI / 2, -Math.PI / 2);
      ctx.fill();

      const circleRadius = 5;
      const circleX = pillX + 15;
      ctx.globalAlpha = 1;
      ctx.beginPath();
      ctx.arc(circleX, pillY, circleRadius, 0, 2 * Math.PI);
      ctx.fill();

      ctx.fillStyle = "#374151";
      ctx.font = BOLD_TEXT_FONT;
      ctx.textAlign = "start";
      ctx.fillText(entity.title, circleX + circleRadius + 10, pillY + 5);
    } else {
      if (startXScreen < -50 || startXScreen > width + 50) return;

      const color = getCategoryColor(entity.categoryNames ?? entity.categories);
      const isHovered = hoveredEntity?.id === entity.id;
      const circleRadius = 5;
      const outerRadius = 12;

      ctx.fillStyle = color;
      ctx.globalAlpha = 0.3;
      ctx.beginPath();
      ctx.arc(startXScreen, rowY, outerRadius, 0, 2 * Math.PI);
      ctx.fill();

      ctx.fillStyle = isHovered ? "#6EE7B7" : color;
      ctx.globalAlpha = 1;
      ctx.beginPath();
      ctx.arc(startXScreen, rowY, circleRadius, 0, 2 * Math.PI);
      ctx.fill();

      ctx.fillStyle = "#374151";
      ctx.font = BOLD_TEXT_FONT;
      ctx.textAlign = "start";
      ctx.fillText(entity.title, startXScreen + outerRadius + 5, rowY + 5);
    }

    ctx.globalAlpha = 1;
  });

  ctx.restore();
};

const drawTooltip = (params: RenderParams) => {
  const { ctx, width, height, hoveredEntity, mousePos, yearToX, viewState, labels } = params;
  if (!hoveredEntity) return;

  let base = mousePos;
  if ((base.x === 0 && base.y === 0) || Number.isNaN(base.x) || Number.isNaN(base.y)) {
    base = entityToScreen(hoveredEntity, yearToX, viewState);
  }

  const padding = 10;
  const lineGap = 18;
  const title = hoveredEntity.title || "";
  const startYearVal = hoveredEntity.start?.year;
  const endYearVal = hoveredEntity.end?.year;
  const hasEnd = typeof startYearVal === "number" && typeof endYearVal === "number" && endYearVal !== startYearVal;
  const categoriesText =
    hoveredEntity.categories && hoveredEntity.categories.length > 0 ? hoveredEntity.categories.join(", ") : "";
  const isPerson = (hoveredEntity.categoryNames?.[0] ?? hoveredEntity.categories?.[0] ?? "").toLowerCase() === "person";
  const age = hasEnd && startYearVal !== undefined && endYearVal !== undefined ? endYearVal - startYearVal : undefined;

  const formatYear = (year: number) => (year < 0 ? `${Math.abs(year).toLocaleString()} BC` : `${year}`);
  const lines: string[] = [
    title,
    typeof startYearVal === "number" ? labels.startYear(formatYear(startYearVal)) : "",
    hasEnd ? labels.endYear(formatYear(endYearVal)) : "",
    isPerson && typeof age === "number" ? labels.age(age) : "",
    categoriesText ? labels.categories(categoriesText) : "",
  ].filter(Boolean);

  ctx.font = `${TEXT_FONT_PX}px ${TEXT_FONT_FAMILY}`;
  const textWidths = lines.map((t) => ctx.measureText(t).width);
  const tooltipWidth = Math.max(200, Math.max(...textWidths) + padding * 2);
  const tooltipHeight = padding * 2 + lines.length * lineGap;

  let tooltipX = base.x + 12;
  let tooltipY = base.y + 12;
  if (tooltipX + tooltipWidth > width) tooltipX = width - tooltipWidth - 4;
  if (tooltipY + tooltipHeight > height) tooltipY = height - tooltipHeight - 4;
  if (tooltipX < 0) tooltipX = 0;
  if (tooltipY < 0) tooltipY = 0;

  const r = 8;
  ctx.beginPath();
  ctx.moveTo(tooltipX + r, tooltipY);
  ctx.lineTo(tooltipX + tooltipWidth - r, tooltipY);
  ctx.quadraticCurveTo(tooltipX + tooltipWidth, tooltipY, tooltipX + tooltipWidth, tooltipY + r);
  ctx.lineTo(tooltipX + tooltipWidth, tooltipY + tooltipHeight - r);
  ctx.quadraticCurveTo(
    tooltipX + tooltipWidth,
    tooltipY + tooltipHeight,
    tooltipX + tooltipWidth - r,
    tooltipY + tooltipHeight,
  );
  ctx.lineTo(tooltipX + r, tooltipY + tooltipHeight);
  ctx.quadraticCurveTo(tooltipX, tooltipY + tooltipHeight, tooltipX, tooltipY + tooltipHeight - r);
  ctx.lineTo(tooltipX, tooltipY + r);
  ctx.quadraticCurveTo(tooltipX, tooltipY, tooltipX + r, tooltipY);
  ctx.closePath();
  ctx.fillStyle = "#FFFFFF";
  ctx.fill();
  ctx.strokeStyle = "#CBD5E1";
  ctx.lineWidth = 1;
  ctx.stroke();

  ctx.fillStyle = "#1F2937";
  ctx.textAlign = "start";
  lines.forEach((text, i) => {
    ctx.fillText(text, tooltipX + padding, tooltipY + padding + (i + 1) * lineGap - 4);
  });
};

export const renderTimeline = (params: RenderParams): void => {
  const { ctx, width, height, viewState, minYear, maxYear, yearRange, yearToX } = params;

  const dpr = window.devicePixelRatio || 1;
  const canvas = ctx.canvas;
  if (canvas.width !== width * dpr || canvas.height !== height * dpr) {
    canvas.width = width * dpr;
    canvas.height = height * dpr;
    canvas.style.width = `${width}px`;
    canvas.style.height = `${height}px`;
  }
  ctx.setTransform(1, 0, 0, 1, 0, 0);
  ctx.scale(dpr, dpr);
  ctx.clearRect(0, 0, width, height);

  // Header
  const axisY = 26;
  const borderY = axisY + 12;

  ctx.fillStyle = "#FFFFFF";
  ctx.fillRect(0, 0, width, borderY);

  // Year step calculation
  const niceStep = (targetYears: number) => {
    const p10 = 10 ** Math.floor(Math.log10(targetYears));
    const cands = [1, 2, 2.5, 5].map((m) => m * p10);
    return cands.find((c) => c >= targetYears) ?? p10 * 10;
  };

  const targetLabelCount = Math.max(2, Math.floor(width / 160));
  const targetYearsForLabels = Math.max(yearRange / targetLabelCount, 1);
  let step = niceStep(targetYearsForLabels);
  const spanAbs = Math.max(Math.abs(minYear), Math.abs(maxYear));
  const coarseMin = spanAbs >= 1_000_000 ? 10_000 : spanAbs >= 100_000 ? 5_000 : spanAbs >= 10_000 ? 1_000 : 0;
  if (coarseMin > 0 && step < coarseMin) {
    const mult = Math.ceil(coarseMin / step);
    step = step * mult;
    step = niceStep(step);
  }

  const startYear = Math.floor(minYear / step) * step;

  // Horizontal border
  ctx.strokeStyle = "#D1D5DB";
  ctx.lineWidth = 1;
  ctx.beginPath();
  ctx.moveTo(0, borderY);
  ctx.lineTo(width, borderY);
  ctx.stroke();

  // Vertical dashed lines
  ctx.setLineDash([4, 4]);
  ctx.strokeStyle = "#D1D5DB";
  for (let y = startYear; y <= maxYear + step; y += step) {
    const x = yearToX(y) * viewState.scale + viewState.offsetX;
    if (x < 0 || x > width) continue;
    ctx.beginPath();
    ctx.moveTo(x, borderY);
    ctx.lineTo(x, height);
    ctx.stroke();
  }
  ctx.setLineDash([]);

  // Draw entities (clipped below header)
  drawEntities(params);

  // Redraw header overlay
  ctx.globalAlpha = 1;
  ctx.fillStyle = "#FFFFFF";
  ctx.fillRect(0, 0, width, borderY);

  ctx.strokeStyle = "#D1D5DB";
  ctx.lineWidth = 1;
  ctx.beginPath();
  ctx.moveTo(0, borderY);
  ctx.lineTo(width, borderY);
  ctx.stroke();

  // Redraw year labels
  let lastRight = -Infinity;
  for (let year = startYear; year <= maxYear + step; year += step) {
    const x = yearToX(year) * viewState.scale + viewState.offsetX;
    if (x < 0 || x > width) continue;

    ctx.font = BOLD_LABEL_FONT;
    const yearText = year < 0 ? `${Math.abs(year).toLocaleString()} BC` : year.toLocaleString();
    const textW = ctx.measureText(yearText).width;
    const padX = 6;
    const padY = 2;
    const rectW = textW + padX * 2;
    const rectH = LABEL_FONT_PX + 6 + padY * 2;
    const rectX = x - rectW / 2;
    const rectY = axisY - (LABEL_FONT_PX + 6) + 2 - padY;

    if (rectX <= lastRight + 2) continue;
    lastRight = rectX + rectW;

    ctx.fillStyle = "#FFFFFF";
    ctx.fillRect(rectX, rectY, rectW, rectH);

    ctx.fillStyle = "#374151";
    ctx.textAlign = "center";
    ctx.fillText(yearText, x, axisY);

    ctx.strokeStyle = "#374151";
    ctx.beginPath();
    ctx.moveTo(x, axisY + 5);
    ctx.lineTo(x, borderY);
    ctx.stroke();
  }

  // Tooltip
  drawTooltip(params);
};
